// UI actions that combine engine, worker and stores: Auto fix (§11.1) and Match exposure (§11.7).
import { autoFix, applyAutoFix, matchExposure } from './autofix';
import { viewerHooks } from '../ui/canvas/Viewer';
import { fullOutputSize } from '../engine/geometry';
import { decodePool } from '../io/decode/pool';
import { defaultSettings } from '../model/defaults';
import { useEditor } from '../state/editor';
import { useLibrary } from '../state/library';
import { useClipboard } from '../state/clipboard';
import type { LinearImage } from '../engine/cpuPipeline';

/** The active photo rendered at ~512 px with its geometry and everything else zero (for metering). */
export function renderForMetering(): LinearImage | null {
  const id = useLibrary.getState().activeId;
  const c = viewerHooks.controller;
  const src = c?.engine.source;
  if (!id || !c || !src) return null;
  const s = useEditor.getState().get(id);
  const base = defaultSettings();
  base.geometry = s.geometry;
  const full = fullOutputSize(s.geometry, src.width, src.height);
  const k = Math.min(1, 512 / Math.max(full.width, full.height));
  const w = Math.max(1, Math.round(full.width * k)),
    h = Math.max(1, Math.round(full.height * k));
  const t = c.engine.renderPreview(base, w, h);
  const img = { width: w, height: h, data: c.engine.readLinear(t) };
  c.request();
  return img;
}

/** Auto fix (§11.1) on the active photo: one undo step. */
export async function runAutoFix(): Promise<boolean> {
  const id = useLibrary.getState().activeId;
  const img = renderForMetering();
  if (!id || !img) return false;
  const r = autoFix(img);
  useEditor.getState().update(id, (st) => Object.assign(st, applyAutoFix(st, r)));
  return true;
}

/** Crops a small linear image to the photo's crop rect (axis-aligned; rotation is ignored for metering). */
function cropSmall(img: LinearImage, crop: { x: number; y: number; w: number; h: number }): LinearImage {
  const x0 = Math.floor(crop.x * img.width),
    y0 = Math.floor(crop.y * img.height);
  const w = Math.max(1, Math.round(crop.w * img.width)),
    h = Math.max(1, Math.round(crop.h * img.height));
  const out = new Float32Array(w * h * 4);
  for (let y = 0; y < h; y++)
    out.set(
      img.data.subarray(((y0 + y) * img.width + x0) * 4, ((y0 + y) * img.width + x0 + w) * 4),
      y * w * 4,
    );
  return { width: w, height: h, data: out };
}

/** The active photo is the reference; every other selected photo gets a matching exposure. One undo step. */
export async function runMatchExposure(): Promise<number> {
  const lib = useLibrary.getState();
  const refId = lib.activeId;
  if (!refId) return 0;
  const ids = [...lib.selected].filter((id) => id !== refId);
  if (!ids.length) return 0;
  const byId = new Map(lib.photos.map((p) => [p.id, p]));
  const ed = useEditor.getState();
  const load = async (id: string) => {
    const s = ed.get(id);
    return {
      img: cropSmall(await decodePool().small(byId.get(id)!.file, 256), s.geometry.crop),
      settings: s,
    };
  };
  const ref = await load(refId);
  const others = await Promise.all(ids.map(load));
  const exps = matchExposure(ref, others);
  const before = useEditor.getState().updateMany(ids, (s, id) => {
    s.light.exposure = exps[ids.indexOf(id)]!;
  });
  useClipboard
    .getState()
    .setToast({ key: 'matchedN', vars: { n: ids.length }, undo: { n: ids.length, before } });
  return ids.length;
}
